import { SECURITY_NAME_ALIAS, TITLE_TO_ALIAS } from 'common/constants/cap-table/capTable';
import type { HighlightVariant } from 'components/FileViewer/pdf-viewer/highlight-layer/HighlightLayer.props';
import type { CitationReferenceData, MappingReferenceData } from 'services/reference-generator/types';
import type { ReviewEvidenceGroup, ReviewItem, ReviewStatus } from 'store/useReviewStore';
import { sameAiValue } from './aiNumericValue';
import { formatDisplayValue } from './formatAiDisplayValue';
import { matchAiOption, normalizeAiSelectValue } from './resolveAiOptionValue';
import { isAiCreatedCitation } from './securityAiPatches';
import type { RowFormat } from '../../../../common/types/scalarSpreadsheet';
import { type AIFieldMetadata, CapTableCellWithReferences } from '../components/captable-table/CaptableTable.props';

// Anything carrying a review flag - AIFieldMetadata today, a CitationReference,
// or a record with a `status` field. `status` (SCAL-8976) is authoritative when
// present; is_accepted is the legacy fallback (boolean | null | undefined).
type ReviewStatusValue = 'pending' | 'accepted' | 'dismissed';
type AcceptFlagged = { status?: ReviewStatusValue | string | null; is_accepted?: boolean | null } | null | undefined;

// Reverse of TITLE_TO_ALIAS so an alias (e.g. 'issue_price') maps to its human
// label ('Original Issue Price'). Falls back to title-casing the alias.
const ALIAS_TO_TITLE: Record<string, string> = Object.fromEntries(
  Object.entries(TITLE_TO_ALIAS).map(([title, alias]) => [alias, title])
);

const fieldLabelFor = (alias: string): string =>
  ALIAS_TO_TITLE[alias] ?? alias.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

/** Review-card id for one security field. */
export const reviewItemId = (securityId: number, fieldAlias: string): string => `${securityId}:${fieldAlias}`;

/**
 * Display for a SUGGESTED (or baseline) value on the card. Select-backed cells
 * (cell.options - e.g. Underlying Security) resolve the value against their
 * options first (the SHARED resolver: by id, then name - exact, then
 * case-insensitive trimmed) so the card never shows a raw option ID; everything
 * else formats with the cell's own config via the shared formatDisplayValue
 * (which itself maps security-type ids to labels), so card and cell always read
 * the same.
 */
const suggestionDisplayFor = (cell: CapTableCellWithReferences, val: unknown): string => {
  if (val === null || val === undefined || val === '') {
    return '';
  }
  const c = cell as {
    dbType?: string;
    alias?: string;
    format?: RowFormat;
    gridType?: string;
    options?: { id?: unknown; name?: unknown }[];
  };
  // Yes/No + Cash-or-PIK rows carry no options array: normalize their value
  // into the option-index domain first so any spelling ('N', 'FALSE', the model
  // boolean) formats as its label through the shared formatter.
  const display = normalizeAiSelectValue(c.alias, val) ?? val;
  return matchAiOption(c.options, val)?.label ?? formatDisplayValue(display, c.dbType, c.alias, c.format, c.gridType);
};

/**
 * Card HEADLINE value - the analyst reads it as "what am I accepting":
 *   - metadata-backed item: the SUGGESTED value (metadata.value), formatted
 *     with the cell's own config - never the current cell value;
 *   - Merge item: "baseline -> suggested" (both formatted), the same arrow
 *     idiom as the in-grid merge viewer, baseline preferring the durable
 *     previous_value snapshot;
 *   - citation-only items and value-less metadata entries: NO headline value.
 *     There is no structured AI suggestion, so headlining the live cell value
 *     would masquerade the user's own data as an AI finding; the card shows the
 *     citation's extracted text (citedValue) in the value slot instead.
 */
const headlineValueFor = (cell: CapTableCellWithReferences, valueChange: 'merge' | 'new' | undefined): string => {
  const meta = cell.aiFieldMetadata;
  if (meta == null || meta.value === undefined) {
    return '';
  }
  const suggested = suggestionDisplayFor(cell, meta.value);
  if (valueChange !== 'merge') {
    return suggested;
  }
  const baseline = 'previous_value' in meta ? meta.previous_value : cell.value;
  return `${suggestionDisplayFor(cell, baseline) || 'N/A'} \u2192 ${suggested}`;
};

/**
 * Compose the card title. Normally "securityLabel - fieldLabel - displayedValue",
 * but the trailing value is dropped when it equals the security label - the
 * Security-Name field's value IS the security, so "Series D - Security Name - Series D"
 * collapses to "Series D - Security Name".
 */
const titleFor = (securityLabel: string, fieldLabel: string, displayedValue: string): string => {
  const head = `${securityLabel} - ${fieldLabel}`;
  return displayedValue && displayedValue !== securityLabel ? `${head} - ${displayedValue}` : head;
};

/**
 * Normalize an AI field's review state to a review status. A field is:
 *   - 'dismissed' when the metadata is absent, OR status==='dismissed'
 *                 (soft decline keeps the entry but flags it - SCAL-8976)
 *   - 'accepted'  when status==='accepted' (or, legacy, is_accepted is true)
 *   - 'pending'   otherwise
 *
 * `status` (SCAL-8976, the seam left in slice 1) is authoritative when present;
 * is_accepted is the backward-compatible fallback.
 */
export const reviewStatusOf = (meta: AcceptFlagged): ReviewStatus => {
  if (meta == null) {
    return 'dismissed';
  }
  if (meta.status) {
    return meta.status === 'accepted' || meta.status === 'dismissed' ? meta.status : 'pending';
  }
  return meta.is_accepted ? 'accepted' : 'pending';
};

// AI provenance of a citation ref (SCAL-8969/9166): the check lives with the
// backend-mirror patches (securityAiPatches gates its accept settle on it);
// re-exported here so review surfaces keep their single import point.
export { isAiCreatedCitation };

// A citation as citationReviewStatus reads it: the review-flag pair plus the
// provenance fields isAiCreatedCitation keys on. All call sites pass full
// CitationReferenceData objects.
type CitationFlagged =
  | ({ status?: ReviewStatusValue | string | null; is_accepted?: boolean | null } & Pick<
      CitationReferenceData,
      'created_source' | 'generation_job'
    >)
  | null
  | undefined;

/**
 * Review status of a CITATION reference itself. Review is an AI-provenance
 * concept: only an AI-created citation (isAiCreatedCitation) can ever be
 * pending. Human/legacy citations are trusted at birth - accepted no matter
 * what the review flags claim - and only an explicit dismissal, honored
 * regardless of provenance, changes their state. For AI citations an explicit
 * `status` is authoritative; the SCAL-8962 is_accepted mirror is the fallback
 * (false -> pending, true/null/absent -> accepted).
 */
export const citationReviewStatus = (citation: CitationFlagged): ReviewStatus => {
  if (citation?.status === 'dismissed') {
    return 'dismissed';
  }
  if (!isAiCreatedCitation(citation)) {
    return 'accepted';
  }
  if (citation?.status) {
    return citation.status === 'accepted' ? 'accepted' : 'pending';
  }
  return citation?.is_accepted === false ? 'pending' : 'accepted';
};

// A mapping row as mappingReviewStatus reads it: its review status plus the
// provenance that decides whether review applies to it at all.
type MappingFlagged = Pick<MappingReferenceData, 'created_source' | 'status'>;

/**
 * Review status of a cell's Excel MAPPING rows, folded into one card status
 * (SCAL-9667). Review is an AI-provenance concept, as it is for citations:
 * human/legacy rows are bookkeeping and count as accepted, while an AI row carries
 * its own status - absent means a payload from a server without the field, which
 * the backend defaults to accepted. The card takes the least-settled state on the
 * cell: pending if any row still needs a decision, else accepted if any row was
 * accepted, else dismissed (and so also for no rows at all).
 */
export const mappingReviewStatus = (refs: MappingFlagged[] | undefined): ReviewStatus => {
  const statuses = (refs ?? []).map(ref => (ref.created_source === 'ai' ? (ref.status ?? 'accepted') : 'accepted'));
  if (statuses.includes('pending')) {
    return 'pending';
  }
  return statuses.includes('accepted') ? 'accepted' : 'dismissed';
};

/**
 * Provenance axis of the grid's citation badge: true when any LIVE (non-dismissed)
 * citation is AI-created. Shared with the review card's "Citation only" marker so
 * both surfaces colour the same cell alike.
 */
export const hasLiveAiCitation = (refs: CitationReferenceData[] | undefined): boolean =>
  (refs ?? []).some(ref => citationReviewStatus(ref) !== 'dismissed' && isAiCreatedCitation(ref));

/**
 * PDF highlight variant for a citation, derived from the citation's REVIEW STATUS -
 * never from how the user navigated to it (SCAL-8986). Only a citation still
 * pending review reads AI-purple (yellow fill + purple outline); accepted - and
 * dismissed, which is not normally navigable - citations highlight plain yellow.
 */
export const statusToHighlightVariant = (status: ReviewStatus): HighlightVariant =>
  status === 'pending' ? 'ai' : 'citation';

// Per-cell evidence counts: citations = CitationReference rows; aiExtractions = the
// data points the AI extracted behind a value suggestion (token references across the
// cell's citations), so a cell with no suggestion counts none; excel = MappingReference
// rows. Only groups with count > 0 are emitted.
const evidenceFor = (cell: CapTableCellWithReferences): ReviewEvidenceGroup[] => {
  const citations = cell.citationReferences?.length ?? 0;
  const aiExtractions =
    cell.aiFieldMetadata != null
      ? (cell.citationReferences ?? []).reduce((sum, c) => sum + (c.tokenReferences?.length ?? 0), 0)
      : 0;
  const excel = cell.mappingReferences?.length ?? 0;

  const groups: ReviewEvidenceGroup[] = [];
  if (citations > 0) {
    groups.push({ kind: 'citations', count: citations });
  }
  if (aiExtractions > 0) {
    groups.push({ kind: 'aiExtractions', count: aiExtractions });
  }
  if (excel > 0) {
    groups.push({ kind: 'excel', count: excel });
  }
  return groups;
};

/**
 * Merge/New chip derivation: how accepting the field suggestion would change the
 * cell's data. The previous side prefers the entry's durable previous_value
 * baseline (falling back to the live cell value for legacy metadata), then
 * mirrors conditions.tsx's String-compare:
 *   'merge'   = a previous value exists and differs (accept supersedes real data)
 *   'new'     = the cell is empty (the suggestion fills it)
 *   undefined = no field suggestion with a value (citation-only items), or the
 *               suggestion equals the current value (AI-created security cells
 *               carry their values already - nothing is superseded or filled).
 */
export const valueChangeFor = (
  cell: Pick<CapTableCellWithReferences, 'value' | 'aiFieldMetadata' | 'options' | 'alias'>
): 'merge' | 'new' | undefined => {
  const meta = cell.aiFieldMetadata;
  const aiValue = meta?.value;
  if (meta == null || aiValue === undefined) {
    return undefined;
  }
  // Previous side: prefer the durable review-trail baseline the backend stamps
  // on the entry (previous_value; null = the apply created the security, so
  // nothing pre-existed -> 'new'). The live cell value lies after successive
  // applies; it remains the fallback for legacy metadata without the key.
  const prev = 'previous_value' in meta ? meta.previous_value : cell.value;
  if (prev === null || prev === undefined || prev === '') {
    return 'new';
  }
  // Select-backed cells (cell.options - e.g. Underlying Security) store option
  // IDs while the AI suggests the option NAME, and the Yes/No + Cash-or-PIK
  // rows (no options array) store option INDICES while the AI suggests labels:
  // compare in RESOLVED space (raw spellings when nothing resolves) so an echo
  // suggestion is not a merge. Numeric values compare in DOUBLE space
  // (sameAiValue): two decimal spellings of one double (shortest repr vs the
  // column's 15dp expansion) are the same value, not a merge.
  const resolve = (v: unknown) => normalizeAiSelectValue(cell.alias, v) ?? matchAiOption(cell.options, v)?.id ?? v;
  return sameAiValue(resolve(aiValue), resolve(prev)) ? undefined : 'merge';
};

/**
 * Pick the citation to surface as the card's ACTIVE/primary one (SCAL-9162, and the
 * T15/T16 FE remainder for coexisting retained citations). Value-acceptance and
 * citation-acceptance are separable: an analyst can keep the AI value yet REJECT its
 * citation (soft-unlink -> is_accepted=false, via declineRefsForFields). So PREFER a
 * non-rejected citation (is_accepted !== false, i.e. pending or accepted) so a
 * rejected citation drops out of the card's doc-jump / source name while the value
 * (displayedValue) is untouched. Among the eligible set pick the MOST-RECENT by
 * created_at (retained prior-run citations coexist - newest wins), falling back to
 * source order when created_at is absent so the established "first citation" behavior
 * is preserved. When EVERY citation is rejected, fall back to the most-recent rejected
 * one so the card still points at a source rather than nothing.
 */
export const isSuggestionMetadata = (meta: AIFieldMetadata | null | undefined): meta is AIFieldMetadata =>
  meta != null && (meta.value !== undefined || meta.status != null || 'is_accepted' in meta);

export const pickActiveCitation = (refs: CitationReferenceData[] | undefined): CitationReferenceData | undefined => {
  const visible = refs ?? [];
  if (visible.length === 0) {
    return undefined;
  }
  const live = visible.filter(ref => ref.is_accepted !== false);
  const pool = live.length > 0 ? live : visible;
  // String `>` on created_at assumes the backend emits consistent ISO-8601
  // (YYYY-MM-DDTHH:MM:SSZ); mixed formats would compare lexicographically wrong.
  return pool.reduce((best, ref) =>
    ref.created_at && (!best.created_at || ref.created_at > best.created_at) ? ref : best
  );
};

/** Citation-only cards stay pending until every AI citation has been reviewed. */
export const pickReviewCitation = (refs: CitationReferenceData[] | undefined): CitationReferenceData | undefined => {
  const pending = refs?.filter(ref => citationReviewStatus(ref) === 'pending');
  return pickActiveCitation(pending?.length ? pending : refs);
};

/**
 * Derive review cards from the already-parsed cap-table cells. Emits one item per
 * (securityId = columnId, fieldAlias = alias) for every cell carrying review
 * evidence: an AI field suggestion (pending AND accepted AND dismissed) OR any
 * CITATION - analyst-created included (SCAL-9159) - each tagged with its `status`
 * and provenance (`aiCreated`). Manual MAPPING-ONLY cells (Excel "=" mappings
 * with no citation) are excluded entirely (user-ratified noise cut). AI-suggested
 * pending fields keep their field-level review state. Once a value is settled,
 * pending citation or mapping evidence gets its own action without reopening it.
 * Manual/legacy citations are trusted; AI citations awaiting review are pending.
 * A cell listed only for its AI Excel mappings folds those rows' own statuses
 * (mappingReviewStatus).
 *
 * The panel pins pending items on top and sorts reviewed/settled items below; within
 * each section this emission order is preserved, and it follows the cells' insertion
 * order - security by security, fields in the default cap-table row order - which is
 * the ticket's default ordering. The "needs review" count filters to pending only.
 *
 * A cell with NO ai metadata and NO citation is skipped: nothing to review.
 *
 * `documentNameById` (docId -> filename) is optional so this stays a pure,
 * easily-tested function; the wiring passes the DocumentFootnotesContext map.
 */
export const aggregateReviewItems = (
  cells: Record<string, CapTableCellWithReferences>,
  documentNameById?: Map<number, string>
): ReviewItem[] => {
  const all = Object.values(cells ?? {});

  // Per column, the security label is the value of the `name`-alias cell.
  const securityLabelByColumn = new Map<number | string, string>();
  all.forEach(c => {
    if (c.alias === SECURITY_NAME_ALIAS && c.columnId != null && c.value != null) {
      securityLabelByColumn.set(c.columnId, String(c.value));
    }
  });

  const items: ReviewItem[] = [];
  all.forEach(cell => {
    const { columnId, columnIndex, rowNumber, alias, aiFieldMetadata } = cell;
    const fieldMeta = isSuggestionMetadata(aiFieldMetadata) ? aiFieldMetadata : null;
    const fieldStatus = reviewStatusOf(fieldMeta);
    const visibleCitations = cell.citationReferences ?? [];
    const primaryCitation =
      fieldStatus === 'pending' ? pickActiveCitation(visibleCitations) : pickReviewCitation(visibleCitations);
    // The mapping settle is a BATCH over every AI row on the cell: index 0 is pk
    // order, not the row the analyst is ruling on.
    const aiMappings = (cell.mappingReferences ?? []).filter(ref => ref.created_source === 'ai');
    const hasAiMapping = aiMappings.length > 0;
    const citationStatus = citationReviewStatus(primaryCitation);
    const mappingStatus = mappingReviewStatus(aiMappings);
    // Pending citations lead, then pending mappings, then the settled evidence.
    const reviewMappings =
      hasAiMapping && (!primaryCitation || (citationStatus !== 'pending' && mappingStatus === 'pending'));
    const evidenceStatus = reviewMappings ? mappingStatus : citationStatus;
    // Fresh pending evidence owns the card once its value is settled. Omit old
    // value metadata only from this card, never from the actual cell.
    const suggestionMeta = fieldStatus === 'pending' || evidenceStatus !== 'pending' ? fieldMeta : null;
    // Citations-only runs carry informational metadata without a value or review
    // state. Show it without making the field settleable; hide old settled findings.
    const displayMeta = fieldMeta ? suggestionMeta : aiFieldMetadata;
    const visibleCell = { ...cell, aiFieldMetadata: suggestionMeta, citationReferences: visibleCitations };
    const primaryMappingReference = cell.mappingReferences?.[0];
    // Panel eligibility: AI field suggestions, citations, and AI Excel mappings
    // from a citations-only run. Human mapping-only cells stay out (bookkeeping).
    if (
      columnId == null ||
      !alias ||
      (suggestionMeta == null && !primaryCitation && !hasAiMapping && !displayMeta?.evidence)
    ) {
      return;
    }
    // Pending values retain their action; otherwise the selected evidence owns it.
    const status = suggestionMeta != null ? fieldStatus : evidenceStatus;

    const securityId = Number(columnId);
    const securityLabel = securityLabelByColumn.get(columnId) ?? `Security ${securityId}`;
    const fieldLabel = fieldLabelFor(alias);
    const valueChange = valueChangeFor(visibleCell);
    const displayedValue = headlineValueFor(visibleCell, valueChange);
    const persistedEvidence = displayMeta?.evidence;
    const commentary = displayMeta?.commentary;
    // One evidence text for the headline and the detail: the persisted quote when
    // the finding carries one, else the live cited spans.
    const citationTexts = persistedEvidence
      ? [persistedEvidence.quote]
      : visibleCitations.flatMap(c => (c.tokenReferences ?? []).map(t => t.plain_text).filter(Boolean));
    const documentNames = documentNameById
      ? Array.from(
        new Set(
          [...visibleCitations, ...(cell.mappingReferences ?? [])]
            .map(ref => documentNameById.get(ref.document_reference))
            .filter((n): n is string => Boolean(n))
        )
      )
      : [];

    items.push({
      id: reviewItemId(securityId, alias),
      securityId,
      fieldAlias: alias,
      status,
      // Provenance (SCAL-9159): an AI field suggestion is always AI; reference-only
      // items derive it from the active citation (created_source, job fallback).
      aiCreated: displayMeta != null || isAiCreatedCitation(primaryCitation) || hasAiMapping,
      // Accept/decline settle FIELD metadata, so only AI-suggested fields are
      // field-settleable (thumbs + bulk checkbox).
      canSettle: suggestionMeta != null,
      // Only the evidence awaiting this decision is settled; retain other sources.
      canSettleCitation: suggestionMeta == null && !reviewMappings && primaryCitation != null,
      canSettleMapping: suggestionMeta == null && reviewMappings,
      valueChange,
      // Echo-suggestion dedupe marker (auto-settled as accepted duplicate):
      // either settle surface may carry it.
      duplicate:
        suggestionMeta?.review_reason === 'duplicate' || (!reviewMappings && primaryCitation?.review_reason === 'duplicate'),
      securityLabel,
      fieldLabel,
      columnPosition: columnIndex ?? undefined,
      rowPosition: rowNumber ?? undefined,
      displayedValue,
      // Headline-less items surface the citation's extracted text (first two spans)
      // in the value slot - a STABLE source, never the live cell value, so the card
      // cannot mutate when accept/reject re-aggregations change the cell.
      citedValue: displayedValue ? undefined : citationTexts.slice(0, 2).join(' ... ') || undefined,
      title: titleFor(securityLabel, fieldLabel, displayedValue),
      evidence: evidenceFor(visibleCell),
      commentary: typeof commentary === 'string' ? [commentary] : (commentary ?? []),
      confidence: displayMeta?.confidence ?? null,
      citationTexts,
      citationDocumentName: persistedEvidence?.file_name,
      documentNames,
      // ACTIVE citation backs the slice-3 doc jump; PRIMARY Excel mapping ref backs
      // the Feature C xlsx reveal (undefined for citation-only items).
      primaryCitation,
      citations: visibleCitations,
      primaryMappingReference,
      mappingIds: aiMappings.map(ref => ref.id).filter((id): id is number => typeof id === 'number'),
    });
  });

  return items;
};

export const isReviewItemActionable = (
  item: Pick<ReviewItem, 'canSettle' | 'canSettleCitation' | 'canSettleMapping'>
): boolean => Boolean(item.canSettle || item.canSettleCitation || item.canSettleMapping);

export const pickNextPendingReviewItem = (pending: ReviewItem[], settledId: string): ReviewItem | null => {
  const actionable = pending.filter(isReviewItemActionable);
  const currentIndex = actionable.findIndex(item => item.id === settledId);
  if (currentIndex === -1) {
    return actionable[0] ?? null;
  }
  return actionable[currentIndex + 1] ?? null;
};
